import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layers } from "lucide-react";
import Sidebar from "../components/Sidebar";
import TopHeader from "../components/TopHeader";
import SearchBar from "../components/SearchBar";
import SaveSearchToMenu from "../components/SaveSearchToMenu";
import EmptyState from "../components/EmptyState";
import LeadCard from "../components/LeadCard";
import AuditModal from "../components/AuditModal";
import EmailModal from "../components/EmailModal";
import ManualAddModal from "../components/ManualAddModal";
import BulkSearchModal from "../components/BulkSearchModal";
import ScoreBreakdownModal from "../components/ScoreBreakdownModal";
import SimilarLeadsModal from "../components/SimilarLeadsModal";
import SequenceModal from "../components/SequenceModal";
import OutreachConfigModal from "../components/OutreachConfigModal";
import { useLang } from "../i18n/LangContext";
import { rememberSearchContext } from "../components/TrialLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Prospecting() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { t } = useLang();

  const [leads, setLeads] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [presetType, setPresetType] = useState("");
  const [presetLoc, setPresetLoc] = useState("");

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLead, setAuditLead] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailLead, setEmailLead] = useState(null);
  const [email, setEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [scoreLead, setScoreLead] = useState(null);
  const [similarLead, setSimilarLead] = useState(null);
  const [sequenceLead, setSequenceLead] = useState(null);
  const [outreachConfigOpen, setOutreachConfigOpen] = useState(false);
  const [lastQuery, setLastQuery] = useState(null);

  useEffect(() => {
    axios.get(`${API}/business-types`).then(({ data }) => setBusinessTypes(data.types || []))
      .catch(() => setBusinessTypes([]));
    // `axios` and `API` are module-level constants — intentionally empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search when arriving from Config "Executar Agora"
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    const bt = params.get("business_type");
    const loc = params.get("location") || "";
    const auto = params.get("auto");
    if (bt) {
      setPresetType(bt);
      setPresetLoc(loc);
      if (auto === "1") {
        runSearch({ business_type: bt, location: loc, radius: "20km" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.search]);

  const runSearch = async ({
    business_type = "", location = "", radius = "20km", status = "",
    rating_min = 0, reviews_min = 0, score_max = 100, has_website = "",
  }) => {
    rememberSearchContext({ sector: business_type, city: location });
    setLastQuery({ business_type, location, radius });
    setSearchLoading(true);
    try {
      const { data } = await axios.get(`${API}/search-leads`, {
        params: {
          business_type, location, radius, status,
          rating_min, reviews_min, score_max, has_website,
        },
      });
      setLeads(data.leads || []);
      setSearched(true);
      const filteredOut = data.filtered_out || 0;
      if (filteredOut > 0) {
        toast.success(t("toast.found_filtered", { count: data.count, hidden: filteredOut }));
      } else {
        toast.success(t("toast.found", { count: data.count }));
      }
    } catch (e) {
      toast.error(t("toast.search_failed"));
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSave = async (lead) => {
    try {
      await axios.post(`${API}/save-lead`, lead);
      toast.success(t("toast.saved"));
    } catch (e) {
      toast.error(t("toast.save_failed"));
    }
  };

  const handleAudit = async (lead) => {
    setAuditLead(lead);
    setAudit(null);
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const { data } = await axios.post(`${API}/run-audit`, {
        lead_id: lead.id, name: lead.name, website: lead.website,
        address: lead.address, sector: lead.sector,
        digital_score: lead.digital_score,
      });
      setAudit(data.audit);
    } catch (e) {
      toast.error(t("toast.audit_failed"));
      setAuditOpen(false);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleEmail = async (lead, opts = {}) => {
    setEmailLead(lead);
    setEmail(null);
    setEmailOpen(true);
    setEmailLoading(true);
    try {
      const { data } = await axios.post(`${API}/generate-email`, {
        lead_id: lead.id, name: lead.name,
        sector: lead.sector, address: lead.address,
        language: opts.language || "en",
        tone: opts.tone || "consultative",
        pain_points: [
          !lead.has_website && "no website",
          !lead.ssl_https && "no SSL",
          (lead.review_count || 0) < 50 && "few reviews",
        ].filter(Boolean),
      });
      setEmail({ ...data.email, language: data.language, tone: data.tone });
    } catch (e) {
      toast.error(t("toast.email_failed"));
      setEmailOpen(false);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleMaps = (lead) => {
    if (lead.google_maps_url) {
      window.open(lead.google_maps_url, "_blank");
      return;
    }
    const q = encodeURIComponent(`${lead.name} ${lead.address}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
  };

  const handleManualSubmit = async (form) => {
    setManualSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/manual-lead`, form);
      toast.success("Business added to CRM!");
      setLeads((prev) => [data.lead, ...prev]);
      setSearched(true);
      setManualOpen(false);
    } catch (e) {
      toast.error("Failed to add business");
    } finally {
      setManualSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <TopHeader onAddManual={() => setManualOpen(true)} />
        <main className="px-8 py-6 max-w-[1400px] mx-auto">
          <div className="mb-6">
            <SearchBar
              onSearch={runSearch}
              loading={searchLoading}
              presetBusinessType={presetType}
              presetLocation={presetLoc}
            />
            <div className="mt-2 flex justify-end gap-2">
              {searched && lastQuery && <SaveSearchToMenu query={lastQuery} />}
              <button
                type="button"
                data-testid="open-outreach-config"
                onClick={() => setOutreachConfigOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-[#E5E7EB] text-[12px] font-semibold text-[#6B7280] hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t("outreach.config_button") || "Email Config"}
              </button>
              <button
                type="button"
                data-testid="open-bulk-search"
                onClick={() => setBulkOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-[#3B82F6] text-[12px] font-semibold text-[#3B82F6] hover:bg-[#EFF6FF] transition-colors"
              >
                <Layers className="w-3.5 h-3.5" /> {t("bulk.open_modal")}
              </button>
            </div>
          </div>

          {!searched ? (
            <EmptyState onAddManual={() => setManualOpen(true)} />
          ) : leads.length === 0 ? (
            <div className="text-center py-16 text-[#6B7280] text-[14px]">
              {t("lead.no_results")}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-[15px] font-semibold text-[#111827]">
                  {leads.length} {t("lead.found")}
                </h2>
                <span className="text-[12px] text-[#6B7280]">
                  {t("lead.found_subtitle")}
                </span>
              </div>
              <div
                data-testid="leads-grid"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                {leads.map((lead, i) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    index={i}
                    onSave={handleSave}
                    onAudit={handleAudit}
                    onEmail={handleEmail}
                    onMaps={handleMaps}
                    onScoreBreakdown={setScoreLead}
                    onSimilar={setSimilarLead}
                    onSequence={setSequenceLead}
                  />
                ))}
              </div>
            </>
          )}
        </main>
      </div>

      <AuditModal
        open={auditOpen}
        onOpenChange={setAuditOpen}
        lead={auditLead}
        audit={audit}
        loading={auditLoading}
      />
      <EmailModal
        open={emailOpen}
        onOpenChange={setEmailOpen}
        lead={emailLead}
        email={email}
        loading={emailLoading}
        onRegenerate={(opts) => emailLead && handleEmail(emailLead, opts)}
      />
      <ManualAddModal
        open={manualOpen}
        onOpenChange={setManualOpen}
        onSubmit={handleManualSubmit}
        submitting={manualSubmitting}
      />
      <BulkSearchModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        businessTypes={businessTypes}
        onResults={(data) => {
          setLeads(data.leads || []);
          setSearched(true);
          setBulkOpen(false);
          toast.success(t("bulk.results_summary", {
            total: data.total_found,
            dedup: data.after_dedup,
            saved: data.auto_saved_qualified_ids?.length || 0,
          }));
        }}
      />
      <ScoreBreakdownModal
        open={!!scoreLead}
        onClose={() => setScoreLead(null)}
        lead={scoreLead}
      />
      <SimilarLeadsModal
        open={!!similarLead}
        onClose={() => setSimilarLead(null)}
        seed={similarLead}
        onPick={(picked) => {
          // When the user picks a similar lead, scroll to the matching card if
          // it's in the current results, otherwise just toast the choice.
          const inResults = leads.find((l) => l.id === picked.id);
          if (inResults) {
            const el = document.querySelector(`[data-testid='lead-card-${picked.id}']`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          } else {
            toast.success(`${picked.name} — abrir em CRM`);
          }
        }}
        onSave={handleSave}
      />
      <SequenceModal
        open={!!sequenceLead}
        onClose={() => setSequenceLead(null)}
        lead={sequenceLead}
      />
      <OutreachConfigModal
        open={outreachConfigOpen}
        onClose={() => setOutreachConfigOpen(false)}
      />
    </div>
  );
}
